import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import "../styles/NotificationCenter.css";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  read: boolean;
  createdAt: string;
}

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  isOpen,
  onClose,
  onUnreadCountChange,
}) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/auth/notifications", {
        params: { limit: 20 }
      });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unreadCount);
      onUnreadCountChange?.(response.data.unreadCount);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  useEffect(() => {
    // Close panel when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await axiosInstance.put(`/auth/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      onUnreadCountChange?.(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axiosInstance.put("/auth/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      onUnreadCountChange?.(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axiosInstance.delete(`/auth/notifications/${id}`);
      const deleted = notifications.find((n) => n.id === id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (deleted && !deleted.read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
        onUnreadCountChange?.(Math.max(0, unreadCount - 1));
      }
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      onClose();
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "job_alert":
        return "💼";
      case "application_update":
        return "📋";
      case "interview_reminder":
        return "🎤";
      case "recommendation":
        return "⭐";
      case "system":
        return "🔔";
      default:
        return "📬";
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="nc-overlay">
      <div className="nc-panel" ref={panelRef}>
        <div className="nc-header">
          <h3>🔔 Notifications</h3>
          <div className="nc-header-actions">
            {unreadCount > 0 && (
              <button onClick={handleMarkAllAsRead} className="nc-mark-all-btn">
                Mark all read
              </button>
            )}
            <button onClick={onClose} className="nc-close-btn">✕</button>
          </div>
        </div>

        <div className="nc-content">
          {loading ? (
            <div className="nc-loading">
              <div className="nc-spinner" />
              <p>Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="nc-empty">
              <span className="nc-empty-icon">📭</span>
              <p>No notifications yet</p>
              <p className="nc-empty-sub">You're all caught up!</p>
            </div>
          ) : (
            <div className="nc-list">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`nc-item ${notification.read ? "read" : "unread"}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="nc-item-icon">{getIcon(notification.type)}</div>
                  <div className="nc-item-content">
                    <h4 className="nc-item-title">{notification.title}</h4>
                    <p className="nc-item-message">{notification.message}</p>
                    <span className="nc-item-time">
                      {formatTime(notification.createdAt)}
                    </span>
                  </div>
                  <button
                    className="nc-item-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(notification.id);
                    }}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="nc-footer">
          <button
            onClick={() => {
              navigate("/notification-settings");
              onClose();
            }}
            className="nc-settings-btn"
          >
            ⚙️ Notification Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;
